import { useEffect, useState } from 'react';

/* ============================================================
   useViewportResize
   ------------------------------------------------------------
   A monotonically-increasing tick that bumps whenever the
   viewport changes size — window resize, orientation change,
   visual viewport resize (iOS keyboard, address-bar collapse).

   Canvas components include this tick in their useEffect deps
   so they re-measure and redraw at the new size without us
   manually wiring resize observers in each one.

   On iOS PWA standalone mode this is essential for two cases:
     1. Device rotates → canvas re-fits the image to the new
        viewport.
     2. App is opened from Home Screen the first time after
        install — the chrome differs from the Safari-tab session
        the layout was last measured in.
   ============================================================ */

export function useViewportResize(): number {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let raf = 0;
    const bump = () => {
      // rAF-coalesce so a burst of resize events (iOS Safari fires
      // dozens during rotation) becomes a single re-render.
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTick((t) => t + 1));
    };

    window.addEventListener('resize', bump);
    window.addEventListener('orientationchange', bump);
    // visualViewport tracks the keyboard / address-bar collapse — finer-
    // grained than `resize` on iOS. Guarded for older browsers.
    window.visualViewport?.addEventListener('resize', bump);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', bump);
      window.removeEventListener('orientationchange', bump);
      window.visualViewport?.removeEventListener('resize', bump);
    };
  }, []);

  return tick;
}
